import { useState } from 'react';
import { useAddAffiliateSlabMutation } from '@/store/api/affiliateApi';
import { handleApiError } from '@/store/api/apiErrorHandler';
import { showToastMessage } from '@/utils';

interface Props {
  open: boolean;
  onClose: () => void;
}

const AddCommissionSlabModal = ({ open, onClose }: Props) => {
  const [fromUser, setFromUser] = useState('');
  const [toUser, setToUser] = useState('');
  const [percentage, setPercentage] = useState('');

  const [errors, setErrors] = useState<{
    fromUser?: string;
    toUser?: string;
    percentage?: string;
  }>({});

  const [addSlab, { isLoading }] = useAddAffiliateSlabMutation();

  if (!open) return null;

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!fromUser) newErrors.fromUser = 'Min active user is required';
    if (!toUser) newErrors.toUser = 'Max active user is required';
    if (!percentage) newErrors.percentage = 'Commission percentage is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    if (fromUser > toUser) {
      showToastMessage('error', 'Min users must be less than max users');
      return;
    }

    try {
      await addSlab({
        fromUser: Number(fromUser),
        toUser: Number(toUser),
        percentage: Number(percentage),
      }).unwrap();

      onClose();
      setFromUser('');
      setToUser('');
      setPercentage('');
    } catch (err) {
      handleApiError(err, 'Failed to add commission slab');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md bg-white rounded-md shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-md font-semibold">Add New Commission Slab</h2>
          <button onClick={onClose} className="text-muted-foreground">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 bg-[#EAFFED]">
          <input
            type="number"
            placeholder="Min active user e.g. 21"
            value={fromUser}
            onChange={(e) => {
              setErrors((prevState) => ({ ...prevState, fromUser: '' }));
              setFromUser(e.target.value);
            }}
            className="w-full px-3 py-2 border rounded-md bg-success/15 border border-border no-spinner"
          />
          {errors.fromUser && <p className="mt-1 text-sm text-danger">{errors.fromUser}</p>}

          <input
            type="number"
            placeholder="Max active user e.g. 50"
            value={toUser}
            onChange={(e) => {
              setErrors((prevState) => ({ ...prevState, toUser: '' }));
              setToUser(e.target.value);
            }}
            className="w-full px-3 py-2 border rounded-md bg-success/15 border border-border no-spinner"
          />
          {errors.toUser && <p className="mt-1 text-sm text-danger">{errors.toUser}</p>}

          <input
            type="number"
            placeholder="Commission e.g. 30%"
            value={percentage}
            onChange={(e) => {
              setErrors((prevState) => ({ ...prevState, percentage: '' }));
              setPercentage(e.target.value);
            }}
            className="w-full px-3 py-2 border rounded-md bg-success/15 border border-border no-spinner"
          />
          {errors.percentage && <p className="mt-1 text-sm text-danger">{errors.percentage}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t">
          <button
            onClick={onClose}
            className="px-4 py-1.5 border border-border text-green rounded-md text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-1.5 bg-green text-gold-foreground rounded-md text-sm"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCommissionSlabModal;
